import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { defer, firstValueFrom, from, of as observableOf, Subject } from 'rxjs'
import { catchError, exhaustMap, finalize, map, mergeMap, share } from 'rxjs/operators'
import { resetComposer } from 'services/compose'
import { createPost, getPostData } from 'services/compose/create/createPost'
import {
  activeDraftSelector,
  composerContentItemSelector,
  composerSelectedProfilePpidsSelector
} from 'services/compose/selectors'
import { MediaItemState } from 'services/compose/state'
import { deleteDrafts } from 'services/post/drafts/actions'
import { getPostsCount } from 'services/post/getPosts/actions'
import { enforcePlannedPostsLimit } from 'services/product'
import { message } from 'services/snackbar'
import { POST_TYPES } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import toCamelCase from 'utils/toCamelCase'

export interface CreatePostParams {
  shareType?: 'now' | 'next' | 'queue'
  scheduled?: boolean
  bucketId?: string
  /** Keeps the composer open behind a loading overlay and resets it once the post lands. */
  createNew?: boolean
}

export function useCreatePost(
  files: Record<string, MediaItemState>,
  onPostCreateStarted: (redirectUrl?: string) => void
) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const contentItem = useSelector(composerContentItemSelector)
  const selectedProfilePpids = useSelector(composerSelectedProfilePpidsSelector)
  const draftId = useSelector(activeDraftSelector)?.id
  const [fetchingPosts, setFetchingPosts] = useState(false)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const createPost$ = useRef<Subject<CreatePostParams>>(undefined)

  const create = useCallback((
    shareType?: 'now' | 'next' | 'queue',
    scheduled?: boolean,
    activeDraftId?: string,
    bucketId?: string,
    onBeforeCreate?: (bucketId?: string) => void,
    createNew?: boolean
  ) => {
    const postsCount$ = from(
      dispatch(getPostsCount())
        .unwrap()
        .catch(e => {
          console.log('Failed to get posts count', e)
          return {}
        })
    )
    setFetchingPosts(true)
    return postsCount$.pipe(
      mergeMap(({ totalPostsCount : currentPostsCount }: any) => {
        if (typeof currentPostsCount === 'number') {
          const nextPlannedPostsCount = currentPostsCount + selectedProfilePpids.length
          const withinLimit = dispatch(enforcePlannedPostsLimit(
            nextPlannedPostsCount,
            intl.formatMessage({ id: 'composer.notifications.planned-limit-reached' })
          ))
          if (!withinLimit) {
            return observableOf({ error: true, handled: true })
          }
        }
        const postData = dispatch(getPostData(shareType, scheduled, files as any))
        const createRequest$ = dispatch(createPost(postData, bucketId, Boolean(contentItem))).pipe(share())

        // Keep critical callbacks alive even if composer unmounts right after start.
        createRequest$.subscribe({
          next: () => {
            if (activeDraftId) {
              firstValueFrom(dispatch(deleteDrafts([activeDraftId], selectedProfilePpids, POST_TYPES as any)))
                .catch(() => undefined)
                // EXPL: the create-new path already resets the composer as soon as the request settles and
                // leaves the user typing in it. Resetting again once this round-trip lands would wipe that.
                .then(() => {
                  if (!createNew) {
                    dispatch(resetComposer())
                  }
                })
            }
          },
          // Errors are handled by the main subscription; without this they'd surface as unhandled.
          error: () => undefined
        })
        if (createNew) {
          setIsCreatingNew(true)
        } else if (onBeforeCreate) {
          onBeforeCreate(bucketId)
        }
        return createRequest$
          .pipe(
            map((response: any) => ({ ...response, shareType, postData, createNew })),
            catchError(error => {
              if (!error.response?.error) {
                // EXPL: There's no error data, show generic error message
                dispatch(message(intl.formatMessage({ id: 'errors.create-post' }), 'error'))
                return observableOf({ error: true, handled: true })
              }
              const errorData = toCamelCase(error.response.error)
              return observableOf({ error: errorData, postData } as any)
            })
          )
      }),
      finalize(() => {
        setFetchingPosts(false)
        setIsCreatingNew(false)
      })
    )
  }, [dispatch, intl, files, contentItem, selectedProfilePpids])

  const onBeforeCreate = useCallback((bucketId?: string) => {
    dispatch(message(intl.formatMessage({ id: 'composer.notifications.create-post-started' }), 'info'))
    const redirectUrl = bucketId ? `/posts/buckets/${bucketId}/posts` : undefined
    onPostCreateStarted(redirectUrl)
  }, [dispatch, intl, onPostCreateStarted])

  const handleResponse = useCallback((response: any) => {
    if (response.handled) {
      return
    }

    // EXPL: a per-profile error list. Without this it would fall through and report success.
    if (Array.isArray(response.error)) {
      const details = response.error.map((e: any) => e?.message).filter(Boolean).join(', ')
      dispatch(message(details || intl.formatMessage({ id: 'errors.create-post' }), 'error'))
      return
    }

    if (response.error) {
      const { message: msg, messageKey, data } = response.error
      if (data && response.postData) {
        const newPostsCount = response.postData.ppPageIds.length
        const nextPlannedPostsCount = data.plannedPostsCount + newPostsCount

        if (nextPlannedPostsCount > data.plannedPostsLimit) {
          dispatch(enforcePlannedPostsLimit(
            nextPlannedPostsCount,
            intl.formatMessage({ id: 'composer.notifications.planned-limit-reached' })
          ))
        } else {
          dispatch(message(messageKey ? intl.formatMessage({ id: messageKey, defaultMessage: msg }) : msg, 'error'))
        }
      } else {
        dispatch(message(msg, 'error'))
      }

      return
    }

    switch (response.shareType) {
      case 'queue':
        dispatch(message(intl.formatMessage({ id: 'notifications.post-added-to-plan' }), 'success'))
        break
      case 'next':
        dispatch(message(intl.formatMessage({ id: 'notifications.post-share-next' }), 'success'))
        break
      case 'now':
        dispatch(message(intl.formatMessage({ id: 'notifications.post-posted' }), 'success'))
        break
      default:
        dispatch(message(intl.formatMessage({ id: 'notifications.post-scheduled' }), 'success'))
    }

    if (response.createNew) {
      dispatch(resetComposer())
    }
  }, [dispatch, intl])

  // The submit pipeline must survive re-renders: recreating the Subject would drop the exhaustMap
  // in-flight guard and allow duplicate submits. Changing values are read through refs instead.
  const createRef = useRef(create)
  const draftIdRef = useRef(draftId)
  const onBeforeCreateRef = useRef(onBeforeCreate)
  const handleResponseRef = useRef(handleResponse)

  useEffect(() => {
    createRef.current = create
    draftIdRef.current = draftId
    onBeforeCreateRef.current = onBeforeCreate
    handleResponseRef.current = handleResponse
  })

  useEffect(() => {
    const subject = new Subject<CreatePostParams>()
    createPost$.current = subject
    subject
      .pipe(
        exhaustMap((params) => defer(
          () => createRef.current(
            params.shareType,
            params.scheduled,
            draftIdRef.current,
            params.bucketId,
            onBeforeCreateRef.current,
            params.createNew
          )
        ).pipe(
          // Contain any error thrown while building or running a request, so the pipeline stays alive.
          catchError(() => {
            dispatch(message(intl.formatMessage({ id: 'errors.create-post' }), 'error'))
            return observableOf({ error: true, handled: true })
          })
        ))
      )
      .subscribe({
        next: (response: any) => handleResponseRef.current(response),
        // Last resort: the pipeline is dead at this point, at least surface the failure.
        error: () => {
          setFetchingPosts(false)
          setIsCreatingNew(false)
          dispatch(message(intl.formatMessage({ id: 'errors.create-post' }), 'error'))
        }
      })

    return () => {
      // Complete source stream so active create requests can finish after composer unmount.
      subject.complete()
    }
  }, [dispatch, intl])

  const submitPost = useCallback((params: CreatePostParams) => {
    createPost$.current?.next(params)
  }, [])

  return { submitPost, fetchingPosts, isCreatingNew }
}
